import { useMemo, useState } from 'react';
import SafeIcon from './SafeIcon';
import { useApp } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { useDemo, useWorkspaceData } from '../store/DemoStore';
import { countFiles, driveTree, type DriveFile } from '../data/drive';

const FILES_PER_PAGE = 60;

export default function DriveBrowser({
  mode,
  onClose,
}: {
  mode: 'connect' | 'browse';
  onClose: () => void;
}) {
  const { translate, readOnlyProps, readOnly } = useApp();
  const { route, notify } = useUi();
  const { commands } = useDemo();
  const { workspace, drive } = useWorkspaceData(route.workspaceId);
  const [step, setStep] = useState<'choose' | 'preview'>('choose');
  const [source, setSource] = useState<'folder' | 'link'>('folder');
  const [folderName, setFolderName] = useState(
    drive.connected ? drive.folder : `${workspace.name} Marketing`
  );
  const [sharedLink, setSharedLink] = useState('');
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const ownerFirstName = workspace.owner.split(' ')[0].toLowerCase();
  const account = drive.account || `${ownerFirstName}@${workspace.id}.example`;
  const googleDriveFolderLinkPattern =
    /^https:\/\/drive\.google\.com\/(drive\/(u\/\d+\/)?folders\/|open\?id=)[\w-]{10,}/;
  const isValidSharedLink = googleDriveFolderLinkPattern.test(sharedLink.trim());
  const canCheckAccess = source === 'link' ? isValidSharedLink : !!folderName.trim();
  const root =
    source === 'link'
      ? translate('Shared folder')
      : folderName.trim() || `${workspace.name} Marketing`;
  const previewTree = useMemo(() => driveTree(workspace.id, root), [workspace.id, root]);

  if (mode === 'browse') return <BrowseSheet onClose={onClose} />;

  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="drawer drive-connect"
        role="dialog"
        aria-modal="true"
        aria-labelledby="drive-connect-title"
      >
        <div className="drawer-header">
          <div>
            <h2 id="drive-connect-title">
              {translate(drive.connected ? 'Change Drive folder' : 'Connect Google Drive')}
            </h2>
            <div className="muted">
              {translate('Step {n} of 2', { n: step === 'choose' ? 1 : 2 })}
            </div>
          </div>
          <button className="icon-button" onClick={onClose} aria-label={translate('Close')}>
            <SafeIcon name="FiX" />
          </button>
        </div>
        {step === 'choose' && (
          <>
            <div className="notice-banner">
              <SafeIcon name="FiShield" />
              <span>
                {translate(
                  'AMY asks for read-only access to one folder. It cannot change or delete anything in your Drive.'
                )}
              </span>
            </div>
            <fieldset className="drive-source">
              <legend>{translate('What should AMY use?')}</legend>
              <label className={`choice-card ${source === 'folder' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="drive-source"
                  checked={source === 'folder'}
                  onChange={() => setSource('folder')}
                />
                <span>
                  <strong>{translate('A folder in my Drive')}</strong>
                  <span className="muted">{translate('Signed in as {account}', { account })}</span>
                </span>
              </label>
              {source === 'folder' && (
                <label className="drive-field">
                  {translate('Folder name')}
                  <input
                    className="text-field"
                    value={folderName}
                    onChange={(event) => setFolderName(event.target.value)}
                  />
                </label>
              )}
              <label className={`choice-card ${source === 'link' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="drive-source"
                  checked={source === 'link'}
                  onChange={() => setSource('link')}
                />
                <span>
                  <strong>{translate('A shared folder link')}</strong>
                  <span className="muted">
                    {translate('For a folder someone else owns, like your photographer.')}
                  </span>
                </span>
              </label>
              {source === 'link' && (
                <label className="drive-field">
                  {translate('Folder link')}
                  <input
                    className="text-field"
                    value={sharedLink}
                    onChange={(event) => setSharedLink(event.target.value)}
                    placeholder="https://drive.google.com/drive/folders/…"
                    aria-invalid={!!sharedLink && !isValidSharedLink}
                  />
                  {sharedLink && !isValidSharedLink && (
                    <span className="field-error" role="alert">
                      {translate('This does not look like a Google Drive folder link.')}
                    </span>
                  )}
                </label>
              )}
            </fieldset>
            <div className="button-row" style={{ marginTop: 16 }}>
              <button
                className="button primary"
                disabled={readOnly || !canCheckAccess}
                onClick={() => setStep('preview')}
              >
                {translate('Check access')}
                <SafeIcon name="FiArrowRight" />
              </button>
              <button className="button" onClick={onClose}>
                {translate('Cancel')}
              </button>
            </div>
            {drive.connected && (
              <div className="drive-disconnect">
                {!confirmDisconnect ? (
                  <button
                    className="button danger-outline"
                    onClick={() => setConfirmDisconnect(true)}
                    {...readOnlyProps}
                  >
                    <SafeIcon name="FiLink2" />
                    {translate('Disconnect Drive')}
                  </button>
                ) : (
                  <div className="notice-banner warn">
                    <SafeIcon name="FiAlertTriangle" />
                    <span>
                      {translate(
                        'AMY stops listing Drive files. Photos already used in posts stay in those posts.'
                      )}
                    </span>
                    <button
                      className="button small danger"
                      onClick={() => {
                        commands.disconnectDrive(workspace.id);
                        notify(translate('Google Drive disconnected'));
                        onClose();
                      }}
                    >
                      {translate('Disconnect')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {step === 'preview' && (
          <>
            <div className="notice-banner status-panel-inline ok">
              <SafeIcon name="FiCheckCircle" />
              <span>
                {translate('AMY can open “{folder}”: {n} files in {f} folders.', {
                  folder: root,
                  n: countFiles(previewTree),
                  f: Object.keys(previewTree).length,
                })}
              </span>
            </div>
            <h3>{translate('What AMY will see')}</h3>
            <ul className="drive-scope">
              {Object.values(previewTree)
                .filter((folder) => folder.files.length)
                .map((folder) => (
                  <li key={folder.path}>
                    <SafeIcon name="FiFolder" size={14} />
                    <span>{folder.path.replace(`${root}/`, '')}</span>
                    <span className="muted">
                      {translate('{n} files', { n: folder.files.length })}
                    </span>
                  </li>
                ))}
            </ul>
            <ul className="brand-list small-print">
              <li>{translate('Only this folder and the folders inside it.')}</li>
              <li>
                {translate(
                  'Files are listed, not copied. AMY prepares a copy only when a file is used in a post.'
                )}
              </li>
              <li>
                {translate('New files show up when the folder is checked, at least once a day.')}
              </li>
            </ul>
            <div className="button-row" style={{ marginTop: 16 }}>
              <button
                className="button primary"
                onClick={() => {
                  commands.connectDrive(workspace.id, root, account, countFiles(previewTree));
                  notify(translate('Google Drive connected: {folder}', { folder: root }));
                  onClose();
                }}
                {...readOnlyProps}
              >
                <SafeIcon name="FiLink" />
                {translate('Connect this folder')}
              </button>
              <button className="button" onClick={() => setStep('choose')}>
                <SafeIcon name="FiArrowLeft" />
                {translate('Back')}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

export function DriveFolderView({
  workspaceId,
  root,
  selected,
  onToggle,
  kinds,
}: {
  workspaceId: string;
  root: string;
  selected: string[];
  onToggle: (file: DriveFile) => void;
  kinds?: string[];
}) {
  const { translate } = useApp();
  const { brand } = useWorkspaceData(workspaceId);
  const brandAssets = brand.active.assets;
  const logos = useMemo(
    () =>
      brandAssets
        .filter((asset) => asset.kind !== 'Document' && asset.src)
        .map((asset) => ({ file: asset.file, src: asset.src as string })),
    [brandAssets]
  );
  const tree = useMemo(() => driveTree(workspaceId, root, logos), [workspaceId, root, logos]);
  const [path, setPath] = useState(root);
  const [shownFileCount, setShownFileCount] = useState(FILES_PER_PAGE);
  const folder = tree[path] ?? tree[root];
  const openFolder = (folderPath: string) => {
    setPath(folderPath);
    setShownFileCount(FILES_PER_PAGE);
  };
  const crumbs = path.split('/').map((name, index, pathParts) => ({
    name,
    path: pathParts.slice(0, index + 1).join('/'),
  }));
  const countFilesIncludingSubfolders = (folderPath: string): number =>
    (tree[folderPath]?.files.length ?? 0) +
    (tree[folderPath]?.children ?? []).reduce(
      (sum, childPath) => sum + countFilesIncludingSubfolders(childPath),
      0
    );
  return (
    <div className="picker-drive">
      <nav className="picker-breadcrumb" aria-label={translate('Folder path')}>
        <SafeIcon name="FiHardDrive" size={14} />
        {crumbs.map((crumb, index) => (
          <span key={crumb.path} className="crumb">
            {index > 0 && <SafeIcon name="FiChevronRight" size={12} />}
            {index === crumbs.length - 1 ? (
              <strong>{crumb.name}</strong>
            ) : (
              <button className="link-button" onClick={() => openFolder(crumb.path)}>
                {crumb.name}
              </button>
            )}
          </span>
        ))}
      </nav>
      {folder.children.length > 0 && (
        <div className="picker-folders">
          {folder.children.map((child) => (
            <button key={child} className="picker-folder" onClick={() => openFolder(child)}>
              <SafeIcon name="FiFolder" size={20} />
              <span>{tree[child].name}</span>
              <span className="muted">
                {translate('{n} files', { n: countFilesIncludingSubfolders(child) })}
              </span>
            </button>
          ))}
        </div>
      )}
      {folder.files.length > 0 && (
        <>
          <p className="muted small-print">
            {translate('{n} files in this folder', { n: folder.files.length })}
          </p>
          <div className="picker-grid">
            {folder.files.slice(0, shownFileCount).map((file) => {
              const isSelected = selected.includes(file.name);
              const isAllowedKind = !kinds || kinds.includes(file.kind);
              return (
                <button
                  key={file.id}
                  className={`picker-tile ${isSelected ? 'selected' : ''}`}
                  disabled={!isAllowedKind}
                  aria-pressed={isSelected}
                  onClick={() => onToggle(file)}
                >
                  <span className="picker-thumb">
                    {file.image ? (
                      <img
                        src={file.image}
                        alt=""
                        loading="lazy"
                        className={file.image.startsWith('data:image/svg') ? 'contain' : undefined}
                      />
                    ) : (
                      <SafeIcon
                        name={file.kind === 'Document' ? 'FiFileText' : 'FiMusic'}
                        size={26}
                      />
                    )}
                  </span>
                  <span className="picker-name">{file.name}</span>
                  <span className="muted picker-meta">
                    {translate(file.kind)} · {file.sizeMb} MB · {file.modified}
                  </span>
                  {isSelected && (
                    <span className="picker-check">
                      <SafeIcon name="FiCheck" size={14} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {shownFileCount < folder.files.length && (
            <button
              className="button small"
              style={{ marginTop: 8 }}
              onClick={() => setShownFileCount(shownFileCount + FILES_PER_PAGE)}
            >
              {translate('Show more ({n} left)', { n: folder.files.length - shownFileCount })}
            </button>
          )}
        </>
      )}
      {!folder.files.length && !folder.children.length && (
        <p className="muted">{translate('This folder is empty.')}</p>
      )}
    </div>
  );
}

function BrowseSheet({ onClose }: { onClose: () => void }) {
  const { translate, readOnly } = useApp();
  const { route, notify } = useUi();
  const { commands } = useDemo();
  const { workspace, drive, assets } = useWorkspaceData(route.workspaceId);
  const [picked, setPicked] = useState<DriveFile[]>([]);
  const toggle = (file: DriveFile) =>
    setPicked((current) =>
      current.some((pickedFile) => pickedFile.id === file.id)
        ? current.filter((pickedFile) => pickedFile.id !== file.id)
        : [...current, file]
    );
  const alreadyInLibraryCount = picked.filter((file) =>
    assets.some((asset) => asset.file === file.name)
  ).length;
  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="drawer material-picker"
        role="dialog"
        aria-modal="true"
        aria-labelledby="drive-browse-title"
      >
        <div className="drawer-header">
          <div>
            <h2 id="drive-browse-title">Google Drive – {drive.folder}</h2>
            <div className="muted">{translate('Choose files to add to the Material bank')}</div>
          </div>
          <button className="icon-button" onClick={onClose} aria-label={translate('Close')}>
            <SafeIcon name="FiX" />
          </button>
        </div>
        <DriveFolderView
          workspaceId={workspace.id}
          root={drive.folder}
          selected={picked.map((file) => file.name)}
          onToggle={toggle}
        />
        <div className="picker-footer">
          <span className="muted">
            {picked.length
              ? translate('Selected: {n}', { n: picked.length })
              : translate('Nothing selected yet')}
            {alreadyInLibraryCount
              ? ` · ${translate('{n} already in the library', { n: alreadyInLibraryCount })}`
              : ''}
          </span>
          <div className="button-row">
            <button className="button" onClick={onClose}>
              {translate('Cancel')}
            </button>
            <button
              className="button primary"
              disabled={readOnly || !picked.length}
              onClick={() => {
                const linkedAssetIds = commands.useDriveFiles(
                  workspace.id,
                  picked.map((file) => ({
                    name: file.name,
                    kind: file.kind,
                    image: file.image,
                    sizeMb: file.sizeMb,
                  }))
                );
                notify(
                  linkedAssetIds.length
                    ? translate('{n} files linked. AMY is preparing them for posts.', {
                        n: linkedAssetIds.length,
                      })
                    : translate('These files are already in the library.')
                );
                onClose();
              }}
            >
              <SafeIcon name="FiPlus" />
              {translate('Add to Material bank')}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
